import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { HttpUtils } from '@core/utils/http-utils';
import { environment } from '@root/environments/environment';
import { map, Observable } from 'rxjs';
import {
  PricingServiceData,
  PricingServiceDataApiModel,
  PricingServiceDataApiModelData,
} from '../types/pricing.types';
import { DiscountUtilsService } from './discount-utils.service';

/**
 * Pricelist view of a service data row: WSP/EUP prices and the approved
 * discounts applied to it (`/nipt-invoicing/service-data`).
 */
@Injectable({
  providedIn: 'root',
})
export class PricingServiceDataService {
  private http = inject(HttpClient);
  private discountUtilsService = inject(DiscountUtilsService);

  private readonly API_URL = `${environment.apiUrl}/nipt-invoicing`;

  /** Exact match on reference key; null when the pricelist has no such row. */
  public getByReferenceKey(
    referenceKey: string
  ): Observable<PricingServiceData | null> {
    return this.http
      .get<PricingServiceDataApiModel | PricingServiceDataApiModelData[]>(
        `${this.API_URL}/service-data`,
        {
          params: HttpUtils.buildHttpParams().set(
            'reference_key',
            referenceKey
          ),
        }
      )
      .pipe(
        map((model) => {
          const rows = Array.isArray(model) ? model : (model?.data ?? []);
          return rows.length
            ? this.apiModelToPricingServiceData(rows[0])
            : null;
        })
      );
  }

  public apiModelToPricingServiceData(
    model: PricingServiceDataApiModelData
  ): PricingServiceData {
    const toNumber = (value: string | number | null | undefined) =>
      value != null ? Number(value) : null;

    return {
      id: model.id,
      referenceKey: model.reference_key,
      partnerId: model.partner_id,
      wholesalePrice: toNumber(model.wholesale_price),
      discountedPrice: toNumber(model.discounted_price),
      eupPrice: toNumber(model.eup_price),
      discountedEupPrice: toNumber(model.discounted_eup_price),
      bundles: (model.bundles ?? []).map((bundle) => ({
        id: bundle.id,
        name: bundle.internal_name ?? bundle.name,
      })),
      discounts: (model.discounts ?? []).map((discount) =>
        this.discountUtilsService.apiModelToDiscount(discount)
      ),
    };
  }
}
